import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CitationChip,
  ConfidenceBadge,
  RiskBadge,
  StatusBadge,
  VerificationBadge,
  formatCurrency,
  formatDate,
} from "@odingard/design-system";
import { LoadingState } from "@odingard/design-system";
import type { Citation } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

type Tab = "overview" | "terms" | "clauses" | "obligations";

export function CaiContractDetail() {
  const { activeOrg } = useAuth();
  const { contractId } = useParams<{ contractId: string }>();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(() => api.contract(orgId, contractId ?? ""), [orgId, contractId]);
  const [tab, setTab] = useState<Tab>("overview");

  const citationMap = useMemo(() => {
    const m = new Map<string, Citation>();
    (state.data?.citations ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [state.data]);

  function citation(id: string | null) {
    if (!id) return null;
    const c = citationMap.get(id);
    if (!c) return null;
    return <CitationChip locator={c.locator} />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/studio/contract-ai/repository" style={{ fontSize: "var(--ods-text-sm)" }}>
            ← Repository
          </Link>
          <h1 style={{ marginTop: 8 }}>{state.data?.contract.title ?? "Contract"}</h1>
          {state.data && <p>{state.data.contract.counterparty}</p>}
        </div>
        {state.data && (
          <div className="ods-row">
            <RiskBadge level={state.data.contract.riskLevel} />
            <StatusBadge status={state.data.contract.status} />
          </div>
        )}
      </div>

      {state.loading && <LoadingState />}
      {state.error && <CaiError error={state.error} onRetry={state.reload} />}
      {state.data && (
        <>
          <div className="tabs" role="tablist">
            {(["overview", "terms", "clauses", "obligations"] as Tab[]).map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "active" : ""} onClick={() => setTab(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <div className="detail-grid">
              <Card>
                <CardHeader title="Summary" />
                <CardBody>
                  <p>{state.data.contract.summary ?? "No summary available."}</p>
                </CardBody>
              </Card>
              <Card>
                <CardHeader title="Key facts" />
                <CardBody>
                  <dl className="kv">
                    <dt>Type</dt>
                    <dd>{state.data.contract.type}</dd>
                    <dt>Value</dt>
                    <dd>{state.data.contract.valueAmount ? formatCurrency(state.data.contract.valueAmount, state.data.contract.currency) : "Not stated"}</dd>
                    <dt>Renewal</dt>
                    <dd>{state.data.contract.renewalDate ? formatDate(state.data.contract.renewalDate) : "Not set"}</dd>
                    <dt>Verification</dt>
                    <dd>
                      <VerificationBadge state={state.data.contract.verification} />
                    </dd>
                  </dl>
                </CardBody>
              </Card>
            </div>
          )}

          {tab === "terms" && (
            <Card>
              <CardHeader title="Extracted terms" />
              <CardBody>
                <div className="ods-stack">
                  {state.data.terms.map((t) => (
                    <div key={t.id} className="ods-row" style={{ justifyContent: "space-between", borderBottom: "1px solid var(--ods-color-line)", paddingBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{t.label}</div>
                        <div style={{ color: "var(--ods-color-ink-2)" }}>{t.value}</div>
                        <div className="ods-row" style={{ marginTop: 6 }}>
                          {citation(t.citationId)}
                          <VerificationBadge state={t.verification} />
                        </div>
                      </div>
                      <ConfidenceBadge value={t.confidence} />
                    </div>
                  ))}
                  {state.data.terms.length === 0 && <p style={{ color: "var(--ods-color-muted)" }}>No terms extracted.</p>}
                </div>
              </CardBody>
            </Card>
          )}

          {tab === "clauses" && (
            <Card>
              <CardHeader title="Clause analysis" />
              <CardBody>
                <div className="ods-stack">
                  {state.data.clauses.map((cl) => (
                    <div key={cl.id} style={{ borderBottom: "1px solid var(--ods-color-line)", paddingBottom: 12 }}>
                      <div className="ods-row" style={{ justifyContent: "space-between" }}>
                        <strong>{cl.clauseType}</strong>
                        <div className="ods-row">
                          <RiskBadge level={cl.riskLevel} />
                          <ConfidenceBadge value={cl.confidence} />
                        </div>
                      </div>
                      <p style={{ margin: "8px 0", color: "var(--ods-color-ink-2)", fontSize: "var(--ods-text-sm)" }}>{cl.text}</p>
                      {cl.playbookFinding && (
                        <div className="banner banner--warning" style={{ marginBottom: 8 }}>
                          ⚠️ {cl.playbookFinding}
                        </div>
                      )}
                      <div className="ods-row">
                        {citation(cl.citationId)}
                        <VerificationBadge state={cl.verification} />
                      </div>
                    </div>
                  ))}
                  {state.data.clauses.length === 0 && <p style={{ color: "var(--ods-color-muted)" }}>No clauses analyzed.</p>}
                </div>
              </CardBody>
            </Card>
          )}

          {tab === "obligations" && (
            <Card>
              <CardHeader title="Obligations" />
              <CardBody>
                <div className="ods-stack">
                  {state.data.obligations.map((o) => (
                    <div key={o.id} className="ods-row" style={{ justifyContent: "space-between", borderBottom: "1px solid var(--ods-color-line)", paddingBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{o.description}</div>
                        <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                          {o.owner ?? "Unassigned"} · due {o.dueDate ? formatDate(o.dueDate) : "not set"}
                        </span>
                      </div>
                      <div className="ods-row">
                        {o.calendarLinked ? <Badge tone="info">Calendar</Badge> : null}
                        <StatusBadge status={o.status} />
                      </div>
                    </div>
                  ))}
                  {state.data.obligations.length === 0 && <p style={{ color: "var(--ods-color-muted)" }}>No obligations tracked.</p>}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </>
  );
}
